import { createContext, ReactNode, useCallback, useRef, useState } from "react";


export enum NotificationType {
  Success,
  Error,
  Warning
}

export type Notification = {
  id?: number;
  type: NotificationType;
  content: React.ReactNode | string;
  announcement?: string;
} | null;

export type NotificationsValue = {
  notification: Notification;
  addNotification: (n: Notification) => void;
  removeNotification: () => void;
};

export const NotificationContext = createContext<NotificationsValue>({
  notification: null,
  addNotification: () => { },
  removeNotification: () => { },
});

type PermissionsProviderProps = {
  children: ReactNode
}

export const NotificationProvider = (props: PermissionsProviderProps) => {

  const [notification, setNotification] = useState<Notification>(null);
  const timeoutToClose = useRef<number>();
  const notificationId = useRef(0);

  const removeNotification = useCallback(() => {
    setNotification(null);
    timeoutToClose.current && clearTimeout(timeoutToClose.current);
  }, []);

  const addNotification = useCallback((notification: Notification) => {
    timeoutToClose.current && clearTimeout(timeoutToClose.current);
    setNotification(notification ? {
      ...notification,
      id: notificationId.current++,
      announcement: notification.announcement
        || (typeof notification.content === "string" ? notification.content : undefined),
    } : null);

    const waitTime = 15000;
    timeoutToClose.current = window.setTimeout(function () {
      removeNotification();
    }, waitTime);
  }, [removeNotification]);

  const contextValue: NotificationsValue = {
    notification,
    addNotification,
    removeNotification,
  };

  return <NotificationContext.Provider value={contextValue}>{props.children}</NotificationContext.Provider>;
}
